import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Portal,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { TimePicker } from '@mui/lab';
import { styled, alpha } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import FuseLoading from '@fuse/core/FuseLoading';
import client from 'client';
import { showMessage } from 'app/store/fuse/messageSlice';

const PAGE_BG = '#f4f6f8';
const FOOTER_HEIGHT = 64;
const FLOATING_BAR_GAP = 16;

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(22),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  minHeight: '100%',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const PageShell = styled(Box)({
  backgroundColor: PAGE_BG,
  minHeight: '100%',
});

const HeaderSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3.5, 4),
  marginBottom: theme.spacing(3.5),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
}));

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3.5, 4),
  marginBottom: theme.spacing(3.5),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(2.5),
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const TogglePanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  marginBottom: theme.spacing(3),
}));

const ScheduleCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'enabled',
})(({ theme, enabled }) => ({
  padding: theme.spacing(2.5, 3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${enabled ? alpha(theme.palette.primary.main, 0.35) : theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  opacity: enabled ? 1 : 0.85,
}));

const FloatingActionBar = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  right: theme.spacing(3.5),
  bottom: FOOTER_HEIGHT + FLOATING_BAR_GAP + 4,
  left: 'auto',
  zIndex: theme.zIndex.modal - 1,
  width: 'auto',
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2.5),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.paper, 0.98),
  backdropFilter: 'blur(12px)',
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.14)}, 0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`,
  [theme.breakpoints.down('sm')]: {
    right: theme.spacing(1.5),
    bottom: FOOTER_HEIGHT + 12,
    padding: theme.spacing(1, 1.25),
  },
}));

const HeaderActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  borderWidth: 1.5,
  color: theme.palette.text.primary,
  borderColor: alpha(theme.palette.text.primary, 0.35),
  '&:hover': {
    borderWidth: 1.5,
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const PrimaryActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 700,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
}));

const TableHeadCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  whiteSpace: 'nowrap',
}));

const DAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const DAY_PRESETS = [
  { key: 'everyday', label: 'Every day', days: [0, 1, 2, 3, 4, 5, 6] },
  { key: 'weekdays', label: 'Weekdays', days: [1, 2, 3, 4, 5] },
  { key: 'weekends', label: 'Weekends', days: [0, 6] },
];

const TIMEZONE_OPTIONS = [
  'America/Chicago',
  'America/New_York',
  'America/Denver',
  'America/Los_Angeles',
  'America/Bogota',
  'America/Mexico_City',
  'UTC',
];

const DEFAULT_SETTINGS = {
  enabled: false,
  timezone: 'America/Chicago',
  pm2_target: 'all',
  schedules: [],
  notify_on_success: true,
  notify_on_failure: true,
  grace_period_minutes: 15,
  command_retry_attempts: 3,
  catchup_retry_attempts: 3,
  catchup_window_minutes: 180,
};

const STATUS_COLORS = {
  success: 'success',
  failed: 'error',
  missed: 'warning',
  initiated: 'info',
};

const LOGS_ROWS_OPTIONS = [5, 10, 25, 50];

const scheduleToDate = (schedule) => {
  const date = new Date();
  date.setHours(Number(schedule.hour) || 0, Number(schedule.minute) || 0, 0, 0);
  return date;
};

const dateToScheduleTime = (date) => {
  if (!date || Number.isNaN(date.getTime())) return null;
  return { hour: date.getHours(), minute: date.getMinutes() };
};

const formatScheduleTime = (schedule) =>
  scheduleToDate(schedule).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

const formatScheduleTime24 = (schedule) => {
  const hour = String(schedule.hour).padStart(2, '0');
  const minute = String(schedule.minute).padStart(2, '0');
  return `${hour}:${minute}`;
};

const formatDaysSummary = (days = []) => {
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 7) return 'Every day';
  if (sorted.join(',') === '1,2,3,4,5') return 'Weekdays (Mon–Fri)';
  if (sorted.join(',') === '0,6') return 'Weekends (Sat–Sun)';
  return sorted
    .map((day) => DAY_OPTIONS.find((option) => option.value === day)?.label)
    .filter(Boolean)
    .join(', ');
};

const formatTriggerLabel = (value) => {
  if (!value) return '-';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const ScheduleTimePicker = ({ schedule, onChange, disabled }) => (
  <TimePicker
    label='Restart time'
    value={scheduleToDate(schedule)}
    disabled={disabled}
    onChange={(newValue) => {
      const nextTime = dateToScheduleTime(newValue);
      if (nextTime) onChange(nextTime);
    }}
    ampm
    minutesStep={5}
    openTo='hours'
    views={['hours', 'minutes']}
    inputFormat='hh:mm a'
    renderInput={(params) => (
      <TextField
        {...params}
        fullWidth
        size='small'
        InputProps={{
          ...params.InputProps,
          startAdornment: (
            <InputAdornment position='start'>
              <AccessTimeIcon fontSize='small' color='action' />
            </InputAdornment>
          ),
        }}
        sx={{ m: 0 }}
      />
    )}
  />
);

const ScheduleSummaryField = ({ schedule, timezone }) => (
  <TextField
    label='Summary'
    size='small'
    fullWidth
    value={`${formatScheduleTime(schedule)} (${formatScheduleTime24(schedule)}) — ${formatDaysSummary(schedule.days_of_week)} · ${timezone}`}
    InputProps={{ readOnly: true }}
    sx={{
      m: 0,
      '& .MuiInputBase-input': {
        cursor: 'default',
        fontWeight: 600,
        fontSize: '0.8125rem',
      },
    }}
  />
);

const ServerRestartConfig = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10);

  const sortedSchedules = useMemo(
    () =>
      [...(settings.schedules || [])].sort((a, b) => {
        if (a.hour === b.hour) return a.minute - b.minute;
        return a.hour - b.hour;
      }),
    [settings.schedules]
  );

  const activeSchedulesCount = useMemo(
    () => sortedSchedules.filter((schedule) => schedule.enabled).length,
    [sortedSchedules]
  );

  const lastLog = logs[0];

  const notify = useCallback(
    (variant, message) => {
      dispatch(
        showMessage({
          message,
          autoHideDuration: 6000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          variant,
        })
      );
    },
    [dispatch]
  );

  const fetchLogs = useCallback(
    async (page = logsPage, rowsPerPage = logsRowsPerPage) => {
      try {
        setLogsLoading(true);
        const offset = page * rowsPerPage;
        const logsRes = await client.get(
          `/server-restart/logs?limit=${rowsPerPage}&offset=${offset}`
        );
        setLogs(logsRes.data?.rows || []);
        setLogsTotal(logsRes.data?.total || 0);
      } catch (error) {
        notify('error', 'Unable to load execution history');
        console.error(error);
      } finally {
        setLogsLoading(false);
      }
    },
    [logsPage, logsRowsPerPage, notify]
  );

  const fetchSettings = useCallback(async () => {
    const settingsRes = await client.get('/server-restart/settings');
    setSettings({ ...DEFAULT_SETTINGS, ...settingsRes.data });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      await fetchSettings();
    } catch (error) {
      notify('error', 'Unable to load server restart configuration');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [fetchSettings, notify]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchLogs(logsPage, logsRowsPerPage);
  }, [logsPage, logsRowsPerPage, fetchLogs]);

  const updateSettingsField = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const addSchedule = () => {
    const newSchedule = {
      id: uuidv4(),
      label: `Schedule ${(settings.schedules?.length || 0) + 1}`,
      hour: 3,
      minute: 0,
      days_of_week: [0, 1, 2, 3, 4, 5, 6],
      enabled: true,
    };
    updateSettingsField('schedules', [...(settings.schedules || []), newSchedule]);
  };

  const updateSchedule = (scheduleId, patch) => {
    updateSettingsField(
      'schedules',
      (settings.schedules || []).map((schedule) =>
        schedule.id === scheduleId ? { ...schedule, ...patch } : schedule
      )
    );
  };

  const removeSchedule = (scheduleId) => {
    updateSettingsField(
      'schedules',
      (settings.schedules || []).filter((schedule) => schedule.id !== scheduleId)
    );
  };

  const toggleScheduleDay = (scheduleId, day) => {
    const schedule = (settings.schedules || []).find((item) => item.id === scheduleId);
    if (!schedule) return;

    const days = schedule.days_of_week.includes(day)
      ? schedule.days_of_week.filter((value) => value !== day)
      : [...schedule.days_of_week, day].sort((a, b) => a - b);

    updateSchedule(scheduleId, { days_of_week: days });
  };

  const applyDayPreset = (scheduleId, days) => {
    updateSchedule(scheduleId, { days_of_week: [...days] });
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await client.put('/server-restart/settings', settings);
      setSettings({ ...DEFAULT_SETTINGS, ...response.data.settings });

      const reload = response.data.reload;
      if (reload && !reload.success) {
        const errorSummary = (reload.errors || [])
          .map((item) => `${item.label}: ${item.error}`)
          .join(' | ');
        notify(
          'warning',
          `Settings saved, but the schedule failed to activate on the running server (${errorSummary}). Fix the issue above and save again.`
        );
      } else {
        notify('success', 'Configuration saved and active on the server');
      }
    } catch (error) {
      notify('error', error?.response?.data || 'Unable to save settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const runRestartNow = async () => {
    try {
      setRunning(true);
      setConfirmOpen(false);
      await client.post('/server-restart/run-now');
      notify('success', 'Restart initiated. The server will restart shortly.');
      setLogsPage(0);
      setTimeout(() => fetchLogs(0, logsRowsPerPage), 4000);
    } catch (error) {
      notify('error', error?.response?.data || 'Unable to trigger restart');
      console.error(error);
    } finally {
      setRunning(false);
    }
  };

  const handleLogsPageChange = (_event, newPage) => {
    setLogsPage(newPage);
  };

  const handleLogsRowsPerPageChange = (event) => {
    setLogsRowsPerPage(parseInt(event.target.value, 10));
    setLogsPage(0);
  };

  if (loading) {
    return (
      <Box className='flex items-center justify-center w-full h-full' sx={{ minHeight: '60vh' }}>
        <FuseLoading />
      </Box>
    );
  }

  return (
    <PageShell>
      <StyledContainer maxWidth='lg'>
        {/* Header */}
        <HeaderSection elevation={0}>
          <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap={2} flexWrap='wrap'>
            <Box display='flex' gap={2} alignItems='flex-start' flex={1}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                }}
              >
                <RestartAltIcon color='primary' sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant='h4' fontWeight={800} lineHeight={1.2}>
                  Scheduled Server Restart
                </Typography>
                <Typography variant='body2' color='text.secondary' mt={0.75} maxWidth={560}>
                  Manage PM2 restarts, backup windows, and email alerts from the super admin panel.
                </Typography>
              </Box>
            </Box>

            <Stack direction='row' spacing={1.5} flexWrap='wrap'>
              <HeaderActionButton
                variant='outlined'
                startIcon={<EmailIcon />}
                component={NavLinkAdapter}
                to='/email-config'
              >
                Email settings
              </HeaderActionButton>
              <HeaderActionButton
                variant='outlined'
                startIcon={<RefreshIcon />}
                onClick={async () => {
                await fetchData();
                await fetchLogs(logsPage, logsRowsPerPage);
              }}
              >
                Refresh
              </HeaderActionButton>
            </Stack>
          </Box>
        </HeaderSection>

        {/* Summary stats */}
        <Grid container spacing={2.5} mb={3.5}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard elevation={0}>
              <Typography variant='caption' color='text.secondary' fontWeight={600}>
                Scheduler
              </Typography>
              <Chip
                size='small'
                icon={settings.enabled ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                label={settings.enabled ? 'Enabled' : 'Disabled'}
                color={settings.enabled ? 'success' : 'default'}
                sx={{ alignSelf: 'flex-start', mt: 0.5 }}
              />
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard elevation={0}>
              <Typography variant='caption' color='text.secondary' fontWeight={600}>
                Active schedules
              </Typography>
              <Typography variant='h5' fontWeight={700}>
                {activeSchedulesCount}
                <Typography component='span' variant='body2' color='text.secondary' sx={{ ml: 0.5 }}>
                  / {sortedSchedules.length}
                </Typography>
              </Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard elevation={0}>
              <Typography variant='caption' color='text.secondary' fontWeight={600}>
                PM2 target
              </Typography>
              <Typography variant='body1' fontWeight={600} noWrap title={settings.pm2_target}>
                {settings.pm2_target || 'all'}
              </Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard elevation={0}>
              <Typography variant='caption' color='text.secondary' fontWeight={600}>
                Last event
              </Typography>
              <Typography variant='body2' fontWeight={600}>
                {lastLog ? new Date(lastLog.started_at).toLocaleString() : 'No events yet'}
              </Typography>
              {lastLog && (
                <Chip
                  size='small'
                  label={lastLog.status}
                  color={STATUS_COLORS[lastLog.status] || 'default'}
                  sx={{ alignSelf: 'flex-start' }}
                />
              )}
            </StatCard>
          </Grid>
        </Grid>

        <Alert severity='info' sx={{ mb: 3.5, borderRadius: 2, py: 1.75, px: 2.5 }}>
          Configure email recipients in{' '}
          <strong>Email Configuration → Super Admin Level</strong> for{' '}
          <strong>Server Restart Success</strong> and <strong>Server Restart Failure</strong>.
        </Alert>

        {/* General settings */}
        <SectionPaper elevation={0}>
          <SectionHeader>
            <SectionTitle>
              <SettingsIcon color='primary' />
              <Box>
                <Typography variant='h6' fontWeight={700}>
                  General settings
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Core scheduler behavior and notification preferences.
                </Typography>
              </Box>
            </SectionTitle>
          </SectionHeader>

          <TogglePanel elevation={0}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.enabled}
                      onChange={(event) => updateSettingsField('enabled', event.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant='body2' fontWeight={600}>
                        Enable scheduled restarts
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        Activates all enabled schedules below
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.notify_on_success}
                      onChange={(event) =>
                        updateSettingsField('notify_on_success', event.target.checked)
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant='body2' fontWeight={600}>
                        Notify on success
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        Email when restart completes
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.notify_on_failure}
                      onChange={(event) =>
                        updateSettingsField('notify_on_failure', event.target.checked)
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant='body2' fontWeight={600}>
                        Notify on failure
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        Email on missed or failed runs
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              </Grid>
            </Grid>
          </TogglePanel>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size='small'>
                <InputLabel>Timezone</InputLabel>
                <Select
                  label='Timezone'
                  value={settings.timezone}
                  onChange={(event) => updateSettingsField('timezone', event.target.value)}
                >
                  {TIMEZONE_OPTIONS.map((timezone) => (
                    <MenuItem key={timezone} value={timezone}>
                      {timezone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                label='PM2 target'
                size='small'
                fullWidth
                helperText={
                  settings.detected_pm2_app
                    ? `Detected PM2 app: ${settings.detected_pm2_app}. Use "all", that name, or stah-backend-production / stah-backend-development.`
                    : 'Use "all" or the exact PM2 app name from "pm2 list" (e.g. stah-backend-production)'
                }
                value={settings.pm2_target}
                onChange={(event) => updateSettingsField('pm2_target', event.target.value)}
                error={
                  !!settings.detected_pm2_app &&
                  settings.pm2_target &&
                  settings.pm2_target !== 'all' &&
                  settings.pm2_target !== settings.detected_pm2_app &&
                  settings.effective_pm2_target !== settings.pm2_target
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label='Grace period (min)'
                size='small'
                type='number'
                fullWidth
                helperText='Time to wait for the app to confirm it came back online'
                inputProps={{ min: 5, max: 120 }}
                value={settings.grace_period_minutes}
                onChange={(event) =>
                  updateSettingsField('grace_period_minutes', Number(event.target.value))
                }
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant='subtitle2' fontWeight={700} gutterBottom>
            Retry &amp; resilience
          </Typography>
          <Typography variant='body2' color='text.secondary' mb={2}>
            Controls how hard the system tries before giving up on an automatic restart and
            emailing you that manual attention is required.
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
              <TextField
                label='Command retries'
                size='small'
                type='number'
                fullWidth
                helperText='Times "pm2 restart" itself is retried, ~10s apart, per attempt'
                inputProps={{ min: 1, max: 10 }}
                value={settings.command_retry_attempts}
                onChange={(event) =>
                  updateSettingsField('command_retry_attempts', Number(event.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label='Catch-up retries'
                size='small'
                type='number'
                fullWidth
                helperText='Times the watchdog re-triggers a full restart per schedule/day'
                inputProps={{ min: 1, max: 10 }}
                value={settings.catchup_retry_attempts}
                onChange={(event) =>
                  updateSettingsField('catchup_retry_attempts', Number(event.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label='Catch-up window (min)'
                size='small'
                type='number'
                fullWidth
                helperText='How long after the scheduled time retries are allowed'
                inputProps={{ min: 15, max: 1440 }}
                value={settings.catchup_window_minutes}
                onChange={(event) =>
                  updateSettingsField('catchup_window_minutes', Number(event.target.value))
                }
              />
            </Grid>
          </Grid>
        </SectionPaper>

        {/* Schedules */}
        <SectionPaper elevation={0}>
          <SectionHeader>
            <SectionTitle>
              <ScheduleIcon color='primary' />
              <Box>
                <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                  <Typography variant='h6' fontWeight={700}>
                    Restart schedules
                  </Typography>
                  {sortedSchedules.length > 0 && (
                    <Chip
                      size='small'
                      variant='outlined'
                      label={`${activeSchedulesCount} active · ${sortedSchedules.length} total`}
                    />
                  )}
                </Box>
                <Typography variant='body2' color='text.secondary'>
                  Set primary and backup restart windows using the clock picker (AM/PM).
                </Typography>
              </Box>
            </SectionTitle>
            <PrimaryActionButton variant='contained' startIcon={<AddIcon />} onClick={addSchedule}>
              Add schedule
            </PrimaryActionButton>
          </SectionHeader>

          {!sortedSchedules.length ? (
            <Paper
              variant='outlined'
              sx={{ p: 5, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}
            >
              <AccessTimeIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
              <Typography variant='subtitle1' fontWeight={700} gutterBottom>
                No schedules yet
              </Typography>
              <Typography variant='body2' color='text.secondary' mb={2}>
                Add your first restart window to get started.
              </Typography>
              <Button variant='contained' startIcon={<AddIcon />} onClick={addSchedule}>
                Create schedule
              </Button>
            </Paper>
          ) : (
            sortedSchedules.map((schedule, index) => (
              <ScheduleCard key={schedule.id} variant='outlined' enabled={schedule.enabled}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  spacing={2}
                  mb={2}
                >
                  <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                    <Chip size='small' label={`#${index + 1}`} color='primary' />
                    <Chip
                      size='small'
                      label={schedule.enabled ? 'Active' : 'Paused'}
                      color={schedule.enabled ? 'success' : 'default'}
                      variant={schedule.enabled ? 'filled' : 'outlined'}
                    />
                  </Stack>

                  <Stack direction='row' spacing={0.5} alignItems='center' flexShrink={0}>
                    <FormControlLabel
                      control={
                        <Switch
                          size='small'
                          checked={!!schedule.enabled}
                          onChange={(event) =>
                            updateSchedule(schedule.id, { enabled: event.target.checked })
                          }
                        />
                      }
                      label='Enabled'
                      sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                    <Tooltip title='Delete schedule'>
                      <IconButton
                        size='small'
                        color='error'
                        onClick={() => removeSchedule(schedule.id)}
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                <Grid container spacing={2.5} alignItems='flex-end'>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label='Schedule name'
                      size='small'
                      fullWidth
                      value={schedule.label}
                      onChange={(event) =>
                        updateSchedule(schedule.id, { label: event.target.value })
                      }
                      sx={{ m: 0 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <ScheduleTimePicker
                      schedule={schedule}
                      disabled={!schedule.enabled}
                      onChange={(time) => updateSchedule(schedule.id, time)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={5}>
                    <ScheduleSummaryField schedule={schedule} timezone={settings.timezone} />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant='caption' color='text.secondary' fontWeight={600} display='block' mb={1}>
                  Repeat on
                </Typography>
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap mb={1}>
                  {DAY_PRESETS.map((preset) => {
                    const isActive =
                      JSON.stringify([...schedule.days_of_week].sort()) ===
                      JSON.stringify([...preset.days].sort());
                    return (
                      <Chip
                        key={`${schedule.id}-${preset.key}`}
                        label={preset.label}
                        size='small'
                        clickable
                        color={isActive ? 'primary' : 'default'}
                        variant={isActive ? 'filled' : 'outlined'}
                        onClick={() => applyDayPreset(schedule.id, preset.days)}
                      />
                    );
                  })}
                </Stack>
                <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap>
                  {DAY_OPTIONS.map((day) => (
                    <Chip
                      key={`${schedule.id}-${day.value}`}
                      label={day.label}
                      size='small'
                      clickable
                      color={schedule.days_of_week.includes(day.value) ? 'primary' : 'default'}
                      variant={
                        schedule.days_of_week.includes(day.value) ? 'filled' : 'outlined'
                      }
                      onClick={() => toggleScheduleDay(schedule.id, day.value)}
                    />
                  ))}
                </Stack>
              </ScheduleCard>
            ))
          )}
        </SectionPaper>

        {/* Execution history */}
        <SectionPaper elevation={0}>
          <SectionHeader>
            <SectionTitle>
              <HistoryIcon color='primary' />
              <Box>
                <Typography variant='h6' fontWeight={700}>
                  Execution history
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Audit log of scheduled, manual, and missed restart attempts.
                </Typography>
              </Box>
            </SectionTitle>
            <HeaderActionButton
              variant='outlined'
              size='small'
              startIcon={<RefreshIcon fontSize='small' />}
              onClick={() => fetchLogs(logsPage, logsRowsPerPage)}
            >
              Refresh log
            </HeaderActionButton>
          </SectionHeader>

          <TableContainer
            component={Paper}
            variant='outlined'
            sx={{ borderRadius: 2, position: 'relative', minHeight: 200 }}
          >
            {logsLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.6)',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableHeadCell>Started</TableHeadCell>
                  <TableHeadCell>Schedule</TableHeadCell>
                  <TableHeadCell>Trigger</TableHeadCell>
                  <TableHeadCell>Status</TableHeadCell>
                  <TableHeadCell>PM2 target</TableHeadCell>
                  <TableHeadCell>Message</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!logs.length && !logsLoading && (
                  <TableRow>
                    <TableCell colSpan={6} align='center' sx={{ py: 4 }}>
                      <Typography color='text.secondary'>No restart events recorded yet</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(log.started_at).toLocaleString()}
                    </TableCell>
                    <TableCell>{log.schedule_label || '—'}</TableCell>
                    <TableCell>{formatTriggerLabel(log.trigger_type)}</TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        label={log.status}
                        color={STATUS_COLORS[log.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell>{log.pm2_target}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Tooltip title={log.message || ''} placement='top-start'>
                        <Typography variant='body2' noWrap>
                          {log.message || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component='div'
              count={logsTotal}
              page={logsPage}
              onPageChange={handleLogsPageChange}
              rowsPerPage={logsRowsPerPage}
              onRowsPerPageChange={handleLogsRowsPerPageChange}
              rowsPerPageOptions={LOGS_ROWS_OPTIONS}
              labelRowsPerPage='Rows per page:'
            />
          </TableContainer>
        </SectionPaper>

        <Typography variant='caption' color='text.secondary' display='block' mb={3} mt={1} textAlign='center'>
          Requires RUN_CRONS=true. Restarts use a short delay so the event is logged before PM2 recycles the process.
        </Typography>

        <Portal>
          <FloatingActionBar elevation={0}>
            <PrimaryActionButton
              variant='contained'
              startIcon={saving ? <CircularProgress size={18} color='inherit' /> : <SaveIcon />}
              onClick={saveSettings}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save configuration'}
            </PrimaryActionButton>
            <HeaderActionButton
              variant='outlined'
              startIcon={running ? <CircularProgress size={18} /> : <PlayArrowIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={running}
            >
              Run restart now
            </HeaderActionButton>
          </FloatingActionBar>
        </Portal>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth='xs' fullWidth>
          <DialogTitle fontWeight={700}>Confirm manual restart</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will execute <strong>pm2 restart {settings.pm2_target || 'all'}</strong> on the
              server. Active users may experience a brief interruption. Do you want to continue?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmOpen(false)} color='inherit'>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={runRestartNow}
              disabled={running}
              startIcon={running ? <CircularProgress size={16} color='inherit' /> : <RestartAltIcon />}
            >
              Restart now
            </Button>
          </DialogActions>
        </Dialog>
      </StyledContainer>
    </PageShell>
  );
};

export default ServerRestartConfig;
